import React, {useState} from "react";
import {
    Typography,
    Card,
    CardHeader,
    CardBody,
    Button, TabsHeader, Tab, TabsBody, TabPanel, Tabs
} from "@material-tailwind/react";
import { format } from "date-fns";
import DatePicker from 'react-datepicker';
import { es } from "date-fns/locale";
import { PrinterIcon } from "@heroicons/react/24/solid";
import axios from "axios";
import {NumericFormat} from "react-number-format";

const Reports = () => {
    const today = formatDate(new Date());

    const [initDate, setInitDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [displayTitle, setDisplayTitle] = useState(today);
    const [reportType, setReportType] = useState('income'); /* income, online_income, concentrated, university_tax */
    const [invoices, setInvoices] = useState([]);
    const [onlineInvoices, setOnlineInvoices] = useState([]);
    const [concentrated, setInvConcentrated] = useState([]);
    const [universityTaxes, setUniversityTaxes] = useState([]);

    const reportActions = {
        income: () => fetchInvoices(initDate, endDate),
        online_income: () => fetchOnlineInvoices(initDate, endDate),
        concentrated: () => fetchConcentrated(initDate, endDate),
        university_tax: () => fetchUniversityTaxes(initDate, endDate),
    };

    const handleDateChange = () => {
        const formattedStartDate = formatDate(initDate);
        const formattedEndDate = formatDate(endDate);
        const title = initDate || endDate
            ? `${formattedStartDate ? formattedStartDate : '...'} a ${formattedEndDate ? formattedEndDate : '...'}`
            : `${today}`;

        setDisplayTitle(title);

        if (reportActions[reportType]) {
            reportActions[reportType]();
        }
    };

    const fetchInvoices = (start_date, end_date) => {
        axios.get('/reports/get_invoices.json', {
            params: {
                start_date: start_date || '',
                end_date: end_date || ''
            }
        })
            .then(response => {
                const data = response.data;
                setInvoices(data);
            })
            .catch(error => {
                console.error('Error fetching invoices data: ', error);
            });
    }

    const fetchOnlineInvoices = (start_date, end_date) => {
        axios.get('/reports/get_online_invoices.json', {
            params: {
                start_date: start_date || '',
                end_date: end_date || ''
            }
        })
            .then(response => {
                const data = response.data;
                setOnlineInvoices(data);
            })
            .catch(error => {
                console.error('Error fetching invoices data: ', error);
            });
    }

    const fetchConcentrated = (start_date, end_date) => {
        axios.get('/reports/concentrated_report.json', {
            params: {
                start_date: start_date || '',
                end_date: end_date || ''
            }
        })
            .then(response => {
                const data = response.data;
                setInvConcentrated(data);
            })
            .catch(error => {
                console.error('Error fetching invoices data: ', error);
            });
    }

    const fetchUniversityTaxes = (start_date, end_date) => {
        axios.get('/reports/university_tax_report.json', {
            params: {
                start_date: start_date || '',
                end_date: end_date || ''
            }
        })
            .then(response => {
                const data = response.data;
                setUniversityTaxes(data);
            })
            .catch(error => {
                console.error('Error fetching invoices data: ', error);
            });
    }

    const toggleTab = (value) => {
        setReportType(value);

        if (reportActions[reportType]) {
            reportActions[reportType]();
        }
    }

    const reportsTabs = [
        {
            label: "Ingresos",
            value: "income",
        },
        {
            label: "Ingresos en línea",
            value: "online_income",
        },
        {
            label: "Concentrado",
            value: "concentrated",
        },
        {
            label: "4%",
            value: "university_tax",
        },
    ];

    return (
        <>
            <div className="min-h-screen p-8">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-800">
                        Reportes de ingresos
                    </h1>
                    <h5 className="text-md text-blue-gray-900">{displayTitle}</h5>
                </div>
                <div className="align-middle mb-8">
                    <div className="grid  lg:grid-cols-3 sm:grid-cols-1 gap-3 mb-3 items-end">
                        <div className="col-span-1">
                            <DatePicker
                                selected={initDate}
                                onChange={(date) => setInitDate(date)}
                                className="border border-blue-gray-200 rounded px-4 py-2 w-full"
                                popperClassName="!z-40"
                                wrapperClassName="w-full"
                                locale={es}
                                dateFormat="dd/MM/yyyy"
                                placeholderText="Fecha de inicio"
                            />
                        </div>
                        <div className="col-span-1">
                            <DatePicker
                                selected={endDate}
                                onChange={(date) => setEndDate(date)}
                                className="border border-blue-gray-200 rounded px-4 py-2 w-full"
                                popperClassName="!z-40"
                                wrapperClassName="w-full"
                                locale={es}
                                dateFormat="dd/MM/yyyy"
                                placeholderText="Fecha de fin"
                            />
                        </div>
                        <div className="col-span-1">
                            <button
                                className="w-full px-4 py-2 text-white bg-blue-500 hover:bg-blue-600 rounded-md shadow focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
                                onClick={handleDateChange}
                            >
                                Buscar
                            </button>
                        </div>
                    </div>
                </div>
                <Tabs value={reportType}>
                    <TabsHeader>
                        {reportsTabs.map(({label, value}) => (
                            <Tab key={value} value={value}
                                 onClick={() => toggleTab(value)}>
                                {label}
                            </Tab>
                        ))}
                    </TabsHeader>
                    <TabsBody>
                        <TabPanel key={'income'} value={'income'} className='p-0 z-0'>
                            <div className="align-middle pt-4">
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                                    <Button
                                        onClick={() => {
                                            window.open(`/reports/account_distribution.pdf`, '_blank');
                                        }}
                                        type="button"
                                        className="flex justify-center items-center gap-3 w-full px-4 py-2 text-white bg-green-500 hover:bg-green-600 rounded-md shadow focus:ring-2 focus:ring-green-400 focus:ring-offset-2 md:col-start-1"
                                    >
                                        <PrinterIcon className='h-5 w-5' />
                                        Distribución cuentas
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            window.open(`/print_delivery_report.pdf?start_date=${format(initDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`, '_blank');
                                        }}
                                        type="button"
                                        className="flex justify-center items-center gap-3 w-full px-4 py-2 text-white bg-green-500 hover:bg-green-600 rounded-md shadow focus:ring-2 focus:ring-green-400 focus:ring-offset-2 md:col-start-2"
                                    >
                                        <PrinterIcon className='h-5 w-5' />
                                        Recaudación diaria
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            window.open(`reports/invoices_report.pdf?start_date=${format(initDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`, '_blank');
                                        }}
                                        type="button"
                                        className="flex justify-center items-center gap-3 w-full px-4 py-2 text-white bg-green-500 hover:bg-green-600 rounded-md shadow focus:ring-2 focus:ring-green-400 focus:ring-offset-2 md:col-start-3"
                                    >
                                        <PrinterIcon className='h-5 w-5' />
                                        Ingresos
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            window.open(`reports/invoices_report_user.pdf?start_date=${format(initDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`, '_blank');
                                        }}
                                        type="button"
                                        className="flex justify-center items-center gap-3 w-full px-4 py-2 text-white bg-green-500 hover:bg-green-600 rounded-md shadow focus:ring-2 focus:ring-green-400 focus:ring-offset-2 md:col-start-4"
                                    >
                                        <PrinterIcon className='h-5 w-5' />
                                        Ingresos por usuario
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            window.open(`reports/cancelled_invoices_report.pdf?start_date=${format(initDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`, '_blank');
                                        }}
                                        type="button"
                                        className="flex justify-center items-center gap-3 w-full px-4 py-2 text-white bg-green-500 hover:bg-green-600 rounded-md shadow focus:ring-2 focus:ring-green-400 focus:ring-offset-2 md:col-start-4"
                                    >
                                        <PrinterIcon className='h-5 w-5' />
                                        Ingresos cancelados
                                    </Button>
                                </div>
                            </div>

                            <div className="mt-4">
                                <div className="mb-4 grid grid-cols-1 gap-6 xl:grid-cols-1">
                                    <Card
                                        className="overflow-hidden xl:col-span-2 border border-blue-gray-100 shadow-sm">
                                        <CardHeader
                                            floated={false}
                                            shadow={false}
                                            color="transparent"
                                            className="m-0 flex items-center justify-between p-6"
                                        >
                                            <div>
                                                <Typography variant="h6" color="blue-gray" className="mb-1">
                                                    Ingresos
                                                </Typography>
                                            </div>
                                        </CardHeader>
                                        <CardBody className="px-0 pt-0 pb-2">
                                            <div className="overflow-x-auto">
                                            <table className="w-full min-w-[640px] table-auto">
                                                <thead>
                                                <tr>
                                                    {["Folio", "Fecha", "Clave", "Usuario", "Contribuyente", "Forma de pago", "Total"].map(
                                                        (el) => (
                                                            <th
                                                                key={el}
                                                                className="border-b border-blue-gray-50 py-3 px-6 text-left"
                                                            >
                                                                <Typography
                                                                    variant="small"
                                                                    className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                                >
                                                                    {el}
                                                                </Typography>
                                                            </th>
                                                        )
                                                    )}
                                                </tr>
                                                </thead>
                                                <tbody>
                                                { invoices ? (
                                                    invoices.map((invoice, key) => {
                                                        const className = `py-3 px-5 ${
                                                            key === invoices.length - 1
                                                                ? ""
                                                                : "border-b border-blue-gray-50"
                                                        }`;

                                                        return (
                                                            <tr key={invoice.id}>
                                                                <td className={className}>
                                                                    <div className="flex items-center gap-4">
                                                                        <Typography
                                                                            variant="small"
                                                                            color="blue-gray"
                                                                            className="font-bold"
                                                                        >
                                                                            {invoice.id}
                                                                        </Typography>
                                                                    </div>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        {invoice.date}
                                                                    </Typography>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        {invoice.parcel_code}
                                                                    </Typography>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        {invoice.username}
                                                                    </Typography>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        {invoice.contributor_name}
                                                                    </Typography>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        {invoice.payment_type_name}
                                                                    </Typography>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        <NumericFormat
                                                                            value={invoice.total.toFixed(2)}
                                                                            displayType={'text'}
                                                                            prefix={'$'}
                                                                            allowLeadingZeros
                                                                            thousandSeparator=","
                                                                        />
                                                                    </Typography>
                                                                </td>
                                                            </tr>
                                                        )
                                                    })) : (
                                                    <tr>
                                                        <td colSpan='7' className='text-center'>
                                                            <div
                                                                className="flex justify-center items-center text-gray-500 rounded-md py-4">
                                                                No hay registros en la fecha actual.
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                                </tbody>
                                            </table>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </div>
                            </div>

                            {/*<hr className="my-6 border-blueGray-300"/>
                            <div
                                className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                                {statisticsChartsData.map((props) => (
                                    <StatisticsChart
                                        key={props.title}
                                        {...props}
                                        footer={
                                            <Typography
                                                variant="small"
                                                className="flex items-center font-normal text-blue-gray-600"
                                            >
                                                <ClockIcon
                                                    strokeWidth={2}
                                                    className="h-4 w-4 text-blue-gray-400"
                                                />
                                                &nbsp;{props.footer}
                                            </Typography>
                                        }
                                    />
                                ))}
                            </div>*/}
                        </TabPanel>
                        <TabPanel key={'online_income'} value={'online_income'} className='p-0 z-0'>
                            <div className="align-middle pt-4">
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                                    <Button
                                        onClick={() => {
                                            window.open(`reports/online_invoices_report.pdf?start_date=${format(initDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`, '_blank');
                                        }}
                                        type="button"
                                        className="flex justify-center items-center gap-3 w-full px-4 py-2 text-white bg-green-500 hover:bg-green-600 rounded-md shadow focus:ring-2 focus:ring-green-400 focus:ring-offset-2 md:col-start-4"
                                    >
                                        <PrinterIcon className='h-5 w-5' />
                                        Ingresos en línea
                                    </Button>
                                </div>
                            </div>

                            <div className="mt-4">
                                <div className="mb-4 grid grid-cols-1 gap-6 xl:grid-cols-1">
                                    <Card
                                        className="overflow-hidden xl:col-span-2 border border-blue-gray-100 shadow-sm">
                                        <CardHeader
                                            floated={false}
                                            shadow={false}
                                            color="transparent"
                                            className="m-0 flex items-center justify-between p-6"
                                        >
                                            <div>
                                                <Typography variant="h6" color="blue-gray" className="mb-1">
                                                    Ingresos en línea
                                                </Typography>
                                            </div>
                                        </CardHeader>
                                        <CardBody className="px-0 pt-0 pb-2">
                                            <div className="overflow-x-auto">
                                            <table className="w-full min-w-[640px] table-auto">
                                                <thead>
                                                <tr>
                                                    {["Folio", "Fecha", "Clave", "Contribuyente", "Forma de pago", "Total"].map(
                                                        (el) => (
                                                            <th
                                                                key={el}
                                                                className="border-b border-blue-gray-50 py-3 px-6 text-left"
                                                            >
                                                                <Typography
                                                                    variant="small"
                                                                    className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                                >
                                                                    {el}
                                                                </Typography>
                                                            </th>
                                                        )
                                                    )}
                                                </tr>
                                                </thead>
                                                <tbody>
                                                { onlineInvoices ? (
                                                    onlineInvoices.map((invoice, key) => {
                                                        const className = `py-3 px-5 ${
                                                            key === invoices.length - 1
                                                                ? ""
                                                                : "border-b border-blue-gray-50"
                                                        }`;

                                                        return (
                                                            <tr key={invoice.id}>
                                                                <td className={className}>
                                                                    <div className="flex items-center gap-4">
                                                                        <Typography
                                                                            variant="small"
                                                                            color="blue-gray"
                                                                            className="font-bold"
                                                                        >
                                                                            {invoice.id}
                                                                        </Typography>
                                                                    </div>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        {invoice.date}
                                                                    </Typography>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        {invoice.parcel_code}
                                                                    </Typography>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        {invoice.contributor_name}
                                                                    </Typography>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        {invoice.payment_type_name}
                                                                    </Typography>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        <NumericFormat
                                                                            value={invoice.total.toFixed(2)}
                                                                            displayType={'text'}
                                                                            prefix={'$'}
                                                                            allowLeadingZeros
                                                                            thousandSeparator=","
                                                                        />
                                                                    </Typography>
                                                                </td>
                                                            </tr>
                                                        )
                                                    })) : (
                                                    <tr>
                                                        <td colSpan='7' className='text-center'>
                                                            <div
                                                                className="flex justify-center items-center text-gray-500 rounded-md py-4">
                                                                No hay registros en la fecha actual.
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                                </tbody>
                                            </table>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </div>
                            </div>
                        </TabPanel>
                        <TabPanel key={'concentrated'} value={'concentrated'} className='p-0'>
                            <div className="align-middle pt-4">
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                    <Button
                                        onClick={() => {
                                            window.open(`reports/concentrated_report.pdf?start_date=${format(initDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`, '_blank');
                                        }}
                                        type="button"
                                        className="flex justify-center items-center gap-3 w-full px-4 py-2 text-white bg-green-500 hover:bg-green-600 rounded-md shadow focus:ring-2 focus:ring-green-400 focus:ring-offset-2 md:col-start-3 md:col-span-3"
                                    >
                                        <PrinterIcon className='h-5 w-5' />
                                        Concentrado
                                    </Button>
                                </div>
                            </div>

                            <div className="mt-4">
                                <div className="mb-4 grid grid-cols-1 gap-6 xl:grid-cols-1">
                                    <Card
                                        className="overflow-hidden xl:col-span-2 border border-blue-gray-100 shadow-sm">
                                        <CardHeader
                                            floated={false}
                                            shadow={false}
                                            color="transparent"
                                            className="m-0 flex items-center justify-between p-6"
                                        >
                                            <div>
                                                <Typography variant="h6" color="blue-gray" className="mb-1">
                                                    Concentrado
                                                </Typography>
                                            </div>
                                        </CardHeader>
                                        <CardBody className="px-0 pt-0 pb-2">
                                            <div className="overflow-x-auto">
                                            <table className="w-full min-w-[640px] table-auto">
                                                <thead>
                                                <tr>
                                                    {["Tipo", "Cuentas", "Directo", "Rezago", "Recargos", "Tasa Univ.", "Dcto. Anticipado", "Redondeo", "Subtotal"].map(
                                                        (el) => (
                                                            <th
                                                                key={el}
                                                                className="border-b border-blue-gray-50 py-3 px-6 text-left"
                                                            >
                                                                <Typography
                                                                    variant="small"
                                                                    className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                                >
                                                                    {el}
                                                                </Typography>
                                                            </th>
                                                        )
                                                    )}
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {concentrated ? (
                                                    concentrated.map((concentrate, key) => {
                                                        const className = `py-3 px-5 ${
                                                            key === concentrated.length - 1
                                                                ? ""
                                                                : "border-b border-blue-gray-50"
                                                        }`;

                                                        return (
                                                            <tr key={key}>
                                                                <td className={className}>
                                                                    <div className="flex items-center gap-4">
                                                                        <Typography
                                                                            variant="small"
                                                                            color="blue-gray"
                                                                            className="font-bold"
                                                                        >
                                                                            {concentrate.location_type_id}
                                                                        </Typography>
                                                                    </div>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        {concentrate.accounts}
                                                                    </Typography>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        <NumericFormat
                                                                            value={concentrate.sum_tax.toFixed(2)}
                                                                            displayType={'text'}
                                                                            prefix={'$'}
                                                                            allowLeadingZeros
                                                                            thousandSeparator=","
                                                                        />
                                                                    </Typography>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        <NumericFormat
                                                                            value={concentrate.sum_delay_tax.toFixed(2)}
                                                                            displayType={'text'}
                                                                            prefix={'$'}
                                                                            allowLeadingZeros
                                                                            thousandSeparator=","
                                                                        />
                                                                    </Typography>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        <NumericFormat
                                                                            value={concentrate.sum_surcharge.toFixed(2)}
                                                                            displayType={'text'}
                                                                            prefix={'$'}
                                                                            allowLeadingZeros
                                                                            thousandSeparator=","
                                                                        />
                                                                    </Typography>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        <NumericFormat
                                                                            value={concentrate.sum_university_tax.toFixed(2)}
                                                                            displayType={'text'}
                                                                            prefix={'$'}
                                                                            allowLeadingZeros
                                                                            thousandSeparator=","
                                                                        />
                                                                    </Typography>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        <NumericFormat
                                                                            value={concentrate.sum_tax_discounts.toFixed(2)}
                                                                            displayType={'text'}
                                                                            prefix={'$'}
                                                                            allowLeadingZeros
                                                                            thousandSeparator=","
                                                                        />
                                                                    </Typography>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        <NumericFormat
                                                                            value={(concentrate.total_rounded - concentrate.total).toFixed(2)}
                                                                            displayType={'text'}
                                                                            prefix={'$'}
                                                                            allowLeadingZeros
                                                                            thousandSeparator=","
                                                                        />
                                                                    </Typography>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        <NumericFormat
                                                                            value={concentrate.total_rounded.toFixed(2)}
                                                                            displayType={'text'}
                                                                            prefix={'$'}
                                                                            allowLeadingZeros
                                                                            thousandSeparator=","
                                                                        />
                                                                    </Typography>
                                                                </td>
                                                            </tr>
                                                        )
                                                        }
                                                    )) : (
                                                    <tr>
                                                        <td colSpan='8' className='text-center'>
                                                            <div
                                                                className="flex justify-center items-center text-gray-500 rounded-md py-4">
                                                                No hay registros en la fecha actual.
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                                </tbody>
                                            </table>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </div>
                            </div>
                        </TabPanel>
                        <TabPanel key={'university_tax'} value={'university_tax'} className='p-0'>
                            <div className="align-middle pt-4">
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                    <Button
                                        onClick={() => {
                                            window.open(`reports/university_tax_report.pdf?start_date=${format(initDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`, '_blank');
                                        }}
                                        type="button"
                                        className="flex justify-center items-center gap-3 w-full px-4 py-2 text-white bg-green-500 hover:bg-green-600 rounded-md shadow focus:ring-2 focus:ring-green-400 focus:ring-offset-2 md:col-start-3 md:col-span-3"
                                    >
                                        <PrinterIcon className='h-5 w-5' />
                                        Imp. Universitario
                                    </Button>
                                </div>
                            </div>

                            <div className="mt-4">
                                <div className="mb-4 grid grid-cols-1 gap-6 xl:grid-cols-1">
                                    <Card
                                        className="overflow-hidden xl:col-span-2 border border-blue-gray-100 shadow-sm">
                                        <CardHeader
                                            floated={false}
                                            shadow={false}
                                            color="transparent"
                                            className="m-0 flex items-center justify-between p-6"
                                        >
                                            <div>
                                                <Typography variant="h6" color="blue-gray" className="mb-1">
                                                    Impuesto universitario 4%
                                                </Typography>
                                            </div>
                                        </CardHeader>
                                        <CardBody className="px-0 pt-0 pb-2">
                                            <div className="overflow-x-auto">
                                            <table className="w-full min-w-[640px] table-auto">
                                                <thead>
                                                <tr>
                                                    {["Folio", "Fecha", "Clave", "Impuesto", "Imp. Universitario"].map(
                                                        (el) => (
                                                            <th
                                                                key={el}
                                                                className="border-b border-blue-gray-50 py-3 px-6 text-left"
                                                            >
                                                                <Typography
                                                                    variant="small"
                                                                    className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                                >
                                                                    {el}
                                                                </Typography>
                                                            </th>
                                                        )
                                                    )}
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {universityTaxes ? (
                                                    universityTaxes.map((universityTax, key) => {
                                                        const className = `py-3 px-5 ${
                                                            key === universityTaxes.length - 1
                                                                ? ""
                                                                : "border-b border-blue-gray-50"
                                                        }`;

                                                        return (
                                                            <tr key={universityTax.id}>
                                                                <td className={className}>
                                                                    <div className="flex items-center gap-4">
                                                                        <Typography
                                                                            variant="small"
                                                                            color="blue-gray"
                                                                            className="font-bold"
                                                                        >
                                                                            {universityTax.id}
                                                                        </Typography>
                                                                    </div>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        {universityTax.date}
                                                                    </Typography>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        {universityTax.parcel_code}
                                                                    </Typography>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        <NumericFormat
                                                                            value={universityTax.tax.toFixed(2)}
                                                                            displayType={'text'}
                                                                            prefix={'$'}
                                                                            allowLeadingZeros
                                                                            thousandSeparator=","
                                                                        />
                                                                    </Typography>
                                                                </td>
                                                                <td className={className}>
                                                                    <Typography
                                                                        variant="small"
                                                                        className="text-xs font-medium text-blue-gray-600"
                                                                    >
                                                                        <NumericFormat
                                                                            value={universityTax.university_tax.toFixed(2)}
                                                                            displayType={'text'}
                                                                            prefix={'$'}
                                                                            allowLeadingZeros
                                                                            thousandSeparator=","
                                                                        />
                                                                    </Typography>
                                                                </td>
                                                            </tr>
                                                        );
                                                    }
                                                )) : (
                                                    <tr>
                                                        <td colSpan='5' className='text-center'>
                                                            <div
                                                                className="flex justify-center items-center text-gray-500 rounded-md py-4">
                                                                No hay registros en la fecha actual.
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                                </tbody>
                                            </table>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </div>
                            </div>
                        </TabPanel>
                    </TabsBody>
                </Tabs>
            </div>
        </>
    );
};

const formatDate = (dateString) => {
    if (!dateString) return "";

    // Check if it's already a Date object, and if not, parse it.
    const date = new Date(dateString);

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-based, so add 1
    const day = date.getDate().toString().padStart(2, '0'); // Ensure the day is two digits

    return `${day}/${month}/${year}`;
};

export default Reports;
